import type { StoredDraftSummary } from '../persistence';
import { ownerProjectStatusLabel } from '../platform';
import { Heart, Play, Repeat2, Share2, Sparkles } from 'lucide-preact';
import type {
  MyCoasterSummary,
  CoasterViewer,
  DiscoveryCatalogPage,
  PublicCatalogSort,
  PublicCoasterCard,
} from '../platform';

export type HubView = 'home' | 'browse' | 'my-coasters';
export type BrowseCollection = PublicCatalogSort | 'favorites';

interface DiscoveryCardProps {
  readonly card: PublicCoasterCard;
  readonly busy: boolean;
  readonly onOpen: (card: PublicCoasterCard) => void;
  readonly onRemix: (card: PublicCoasterCard) => void;
  readonly onBoost: (card: PublicCoasterCard) => void;
  readonly onFavorite: (card: PublicCoasterCard) => void;
  readonly onCreator: (creatorId: string, displayName: string) => void;
  readonly onShare: (card: PublicCoasterCard) => void;
}

function DiscoveryCard({
  card,
  busy,
  onOpen,
  onRemix,
  onBoost,
  onFavorite,
  onCreator,
  onShare,
}: DiscoveryCardProps) {
  const favoriteLabel = card.viewerState.favorited
    ? `Remove ${card.title} from Favorites`
    : `Add ${card.title} to Favorites`;
  return (
    <article class="discovery-card" data-testid={`coaster-card-${card.publicCoasterId}`}>
      <button class="discovery-card-hero" type="button" onClick={() => onOpen(card)}>
        {card.heroUrl ? (
          <img src={card.heroUrl} alt={`${card.title} coaster overview`} loading="lazy" />
        ) : (
          <span class={`discovery-card-fallback theme-${card.themeId}`} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        )}
        <span class={`intensity-pill intensity-${card.intensity.level}`}>
          {card.intensity.label}
        </span>
        {card.creator.firstParty && <span class="first-party-pill">Official</span>}
      </button>
      <button
        class={`favorite-heart ${card.viewerState.favorited ? 'is-favorited' : ''}`}
        type="button"
        aria-label={favoriteLabel}
        aria-pressed={card.viewerState.favorited}
        disabled={busy || card.viewerState.isOwner}
        title={card.viewerState.isOwner ? 'You made this coaster.' : favoriteLabel}
        onClick={() => onFavorite(card)}
      >
        <Heart
          aria-hidden="true"
          size={19}
          fill={card.viewerState.favorited ? 'currentColor' : 'none'}
        />
      </button>
      <div class="discovery-card-copy">
        <div>
          <strong>{card.title}</strong>
          <button
            class="creator-link"
            type="button"
            disabled={card.creator.deleted}
            onClick={() => onCreator(card.creator.publicCreatorId, card.creator.displayName)}
          >
            {card.creator.deleted ? 'Former Buncha Creator' : `by ${card.creator.displayName}`}
          </button>
        </div>
        <div class="social-proof" aria-label="Coaster popularity">
          <span aria-label={`${card.stats.boosts.toLocaleString()} Boosts`}>
            <Sparkles aria-hidden="true" size={14} /> {card.stats.boosts.toLocaleString()}
          </span>
          <span aria-label={`${card.stats.qualifiedRides.toLocaleString()} rides`}>
            <Play aria-hidden="true" size={14} /> {card.stats.qualifiedRides.toLocaleString()}
          </span>
          <span aria-label={`${card.stats.remixes.toLocaleString()} remixes`}>
            <Repeat2 aria-hidden="true" size={14} /> {card.stats.remixes.toLocaleString()}
          </span>
        </div>
        {card.lineage?.sourceTitle && <small>Remix of {card.lineage.sourceTitle}</small>}
        <div class="discovery-card-actions">
          <button class="primary-action" type="button" disabled={busy} onClick={() => onOpen(card)}>
            <Play aria-hidden="true" size={16} /> Ride
          </button>
          <button
            type="button"
            disabled={busy || !card.allowRemix}
            title={card.allowRemix ? 'Create your own copy' : 'Creator disabled remixing'}
            onClick={() => onRemix(card)}
          >
            <Repeat2 aria-hidden="true" size={16} /> Remix
          </button>
          <button
            type="button"
            aria-pressed={card.viewerState.boosted}
            disabled={busy || card.viewerState.isOwner}
            title={card.viewerState.isOwner ? 'You made this coaster.' : undefined}
            onClick={() => onBoost(card)}
          >
            <Sparkles aria-hidden="true" size={16} />
            {card.viewerState.boosted ? 'Boosted' : 'Boost'}
          </button>
          <button type="button" disabled={busy} onClick={() => onShare(card)}>
            <Share2 aria-hidden="true" size={16} /> Share
          </button>
        </div>
        {!card.allowRemix && <small class="muted-label">Remix unavailable</small>}
      </div>
    </article>
  );
}

export function BrowsePanel({
  page,
  loading,
  error,
  collection,
  remixableOnly,
  creatorLabel,
  pendingIds,
  message,
  onCollection,
  onRemixable,
  onClearCreator,
  onRetry,
  onLoadMore,
  onOpen,
  onRemix,
  onBoost,
  onFavorite,
  onCreator,
  onShare,
}: {
  readonly page: DiscoveryCatalogPage | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly collection: BrowseCollection;
  readonly remixableOnly: boolean;
  readonly creatorLabel: string | null;
  readonly pendingIds: ReadonlySet<string>;
  readonly message: string | null;
  readonly onCollection: (collection: BrowseCollection) => void;
  readonly onRemixable: (enabled: boolean) => void;
  readonly onClearCreator: () => void;
  readonly onRetry: () => void;
  readonly onLoadMore: () => void;
  readonly onOpen: (card: PublicCoasterCard) => void;
  readonly onRemix: (card: PublicCoasterCard) => void;
  readonly onBoost: (card: PublicCoasterCard) => void;
  readonly onFavorite: (card: PublicCoasterCard) => void;
  readonly onCreator: (creatorId: string, displayName: string) => void;
  readonly onShare: (card: PublicCoasterCard) => void;
}) {
  return (
    <section class="discovery-panel" aria-labelledby="browse-title" data-testid="browse-panel">
      <div class="discovery-heading">
        <div>
          <h2 id="browse-title">{creatorLabel ? `More by ${creatorLabel}` : 'Browse & Remix'}</h2>
        </div>
        {creatorLabel && (
          <button type="button" onClick={onClearCreator}>
            Show everyone
          </button>
        )}
      </div>
      <div class="catalog-controls">
        <div role="group" aria-label="Browse coasters">
          {(
            [
              ['most_boosted', 'Most Boosted'],
              ['most_ridden', 'Most Ridden'],
              ['newest', 'Newest'],
              ['favorites', 'Favorites'],
            ] as const
          ).map(([value, label]) => (
            <button
              type="button"
              aria-pressed={collection === value}
              onClick={() => onCollection(value)}
            >
              {label}
              {value === 'most_boosted' &&
                collection === value &&
                page?.rankingWindowLabel &&
                ` · ${page.rankingWindowLabel}`}
            </button>
          ))}
        </div>
        <label>
          <input
            type="checkbox"
            checked={remixableOnly}
            onChange={(event) => onRemixable(event.currentTarget.checked)}
          />
          Remixable only
        </label>
      </div>
      {message && (
        <div class="catalog-message" role="status">
          {message}
        </div>
      )}
      {error && (
        <div class="catalog-empty" role="alert">
          <strong>Browse is taking a break.</strong>
          <span>{error}</span>
          <button type="button" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}
      {loading && !page && <div class="catalog-skeleton" aria-label="Loading coasters" />}
      {!loading && !error && page?.items.length === 0 && (
        <div class="catalog-empty">
          <strong>
            {collection === 'favorites' ? 'No Favorites yet.' : 'No coasters here yet.'}
          </strong>
          <span>
            {collection === 'favorites'
              ? 'Tap the heart on a coaster to save it here.'
              : 'Try another sort or turn off Remixable only.'}
          </span>
        </div>
      )}
      {page && (
        <div class="discovery-grid">
          {page.items.map((card) => (
            <DiscoveryCard
              key={card.publicCoasterId}
              card={card}
              busy={pendingIds.has(card.publicCoasterId)}
              onOpen={onOpen}
              onRemix={onRemix}
              onBoost={onBoost}
              onFavorite={onFavorite}
              onCreator={onCreator}
              onShare={onShare}
            />
          ))}
        </div>
      )}
      {page?.nextCursor && (
        <button class="load-more-action" type="button" disabled={loading} onClick={onLoadMore}>
          {loading ? 'Loading…' : 'Load More'}
        </button>
      )}
    </section>
  );
}

export function MyCoastersPanel({
  viewer,
  published,
  drafts,
  draftPreviewUrls,
  tab,
  loading,
  error,
  onTab,
  onOpenDraft,
  onOpenPublic,
}: {
  readonly viewer: CoasterViewer;
  readonly published: readonly MyCoasterSummary[];
  readonly drafts: readonly StoredDraftSummary[];
  readonly draftPreviewUrls: Readonly<Record<string, string>>;
  readonly tab: 'published' | 'drafts';
  readonly loading: boolean;
  readonly error: string | null;
  readonly onTab: (tab: 'published' | 'drafts') => void;
  readonly onOpenDraft: (projectId: string) => void;
  readonly onOpenPublic: (slug: string) => void;
}) {
  return (
    <section class="discovery-panel" data-testid="my-coasters-panel">
      <div class="discovery-heading">
        <div>
          <h2>My Coasters</h2>
          <p>{viewer.kind === 'authenticated' ? viewer.displayName : 'Saved on this device'}</p>
        </div>
      </div>
      <div class="catalog-controls" role="tablist" aria-label="My coaster collections">
        <button role="tab" aria-selected={tab === 'published'} onClick={() => onTab('published')}>
          Published
        </button>
        <button role="tab" aria-selected={tab === 'drafts'} onClick={() => onTab('drafts')}>
          Drafts
        </button>
      </div>
      {loading && tab === 'published' && (
        <div class="catalog-skeleton" aria-label="Loading My Coasters" />
      )}
      {error && tab === 'published' && (
        <div class="catalog-message" role="alert">
          {error}
        </div>
      )}
      {!loading && tab === 'published' && viewer.kind === 'anonymous' && (
        <div class="catalog-empty">
          <strong>Sign in to see published coasters.</strong>
          <span>Your drafts are still available on this device.</span>
        </div>
      )}
      {!loading && tab === 'published' && viewer.kind === 'authenticated' && (
        <div class="my-coaster-list">
          {published.length ? (
            published.map((coaster) => (
              <article key={coaster.projectId}>
                <div>
                  <strong>{coaster.title}</strong>
                  <small>{ownerProjectStatusLabel(coaster.state).replaceAll('_', ' ')}</small>
                </div>
                <div class="social-proof">
                  {coaster.stats && (
                    <span>
                      <Sparkles aria-hidden="true" size={14} />
                      {coaster.stats.boosts.toLocaleString()}
                    </span>
                  )}
                  {coaster.stats && (
                    <span>
                      <Play aria-hidden="true" size={14} />
                      {coaster.stats.qualifiedRides.toLocaleString()}
                    </span>
                  )}
                  {coaster.publicSlug && (
                    <button type="button" onClick={() => onOpenPublic(coaster.publicSlug!)}>
                      View public
                    </button>
                  )}
                </div>
              </article>
            ))
          ) : (
            <p>No published coasters yet.</p>
          )}
        </div>
      )}
      {tab === 'drafts' && (
        <div class="my-draft-grid">
          {drafts.length ? (
            drafts.map((draft) => (
              <article key={draft.projectId} class={draft.corrupt ? 'is-corrupt' : ''}>
                <div class="my-draft-preview">
                  {draftPreviewUrls[draft.projectId] ? (
                    <img src={draftPreviewUrls[draft.projectId]} alt={`${draft.title} overview`} />
                  ) : (
                    <span aria-hidden="true">
                      <i />
                    </span>
                  )}
                </div>
                <div>
                  <strong>{draft.title}</strong>
                  <small>
                    {draft.corrupt ? draft.errorMessage : new Date(draft.savedAt).toLocaleString()}
                  </small>
                </div>
                <button
                  type="button"
                  disabled={draft.corrupt}
                  onClick={() => onOpenDraft(draft.projectId)}
                >
                  Open Draft
                </button>
              </article>
            ))
          ) : (
            <div class="catalog-empty">
              <strong>No drafts yet.</strong>
              <span>Start a new coaster from Build, then choose Save Draft.</span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
